import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ArrowLeft, User } from 'lucide-react';
import { useHistory } from 'react-router-dom';
import { getUser } from '../../store/user';
import useRestorePrivateUserFields from '../../hooks/useRestorePrivateUserFields';
import SublyApi from '../../helpers/Api';

// Import profile card components
import PersonalInfoCard from './profile-cards/PersonalInfoCard';
import BrokerInfoCard from './profile-cards/BrokerInfoCard';
import BrandingCard from './profile-cards/BrandingCard';
import PersonalDetailsCard from './profile-cards/PersonalDetailsCard';
import SocialMediaCard from './profile-cards/SocialMediaCard';
import SchedulingCard from './profile-cards/SchedulingCard';

// Import onboarding components that work conditionally in both contexts
import OnboardingAddressCard from '../onboarding/components/OnboardingAddressCard';
import OnboardingPhotosCard from '../onboarding/components/OnboardingPhotosCard';
import { useOnboardingImages } from '../onboarding/hooks/useOnboardingImages';

const ProfileEnhanced = () => {
	console.log('🔵 ProfileEnhanced - Component mounting');
	
	const history = useHistory();
	const dispatch = useDispatch();
	const currentUser = useSelector((st) => {
		console.log('🔵 ProfileEnhanced - Redux state.user:', st.user);
		return st.user.currentUser;
	});
	const token = useSelector((st) => st.user.token);
	
	console.log('🔵 ProfileEnhanced - currentUser from Redux:', currentUser);
	console.log('🔵 ProfileEnhanced - token exists:', !!token);

	// State for profile editing
	const [editMode, setEditMode] = useState({});
	const [loading, setLoading] = useState(false);
	const [notification, setNotification] = useState({ type: '', message: '' });
	
	// Image management hook for unified photo experience
	const clientImages = useOnboardingImages(currentUser);

	// CRM login isn't kept in browser storage; reload it after a refresh (#152)
	useRestorePrivateUserFields();

	// Redirect if not onboarded
	useEffect(() => {
		console.log('🔵 ProfileEnhanced - useEffect running, currentUser:', currentUser);
		
		const isOnboardingCompleted = () => {
			if (!currentUser) {
				console.log('🔵 ProfileEnhanced - No currentUser, onboarding not completed');
				return false;
			}
			
			const completed = currentUser.onboardingCompleted === true;

			console.log('🔵 ProfileEnhanced - Onboarding completed check:', {
				onboardingCompleted: currentUser.onboardingCompleted,
				result: completed
			});
			
			return completed;
		};

		if (currentUser && !isOnboardingCompleted()) {
			console.log('🔵 ProfileEnhanced - Redirecting to /forms - onboarding not completed');
			history.push('/forms');
		}
	}, [currentUser, history]);

	// Profile data hook for card components (card-based editing)
	const profileDataHook = {
		// New card-based methods
		updateCard: async (cardData) => {
			setLoading(true);
			setNotification({ type: '', message: '' });

			try {
				// Update user profile via API with all card fields at once
				await SublyApi.request(
					`users/${currentUser.username}`,
					cardData,
					'patch',
					{
						Authorization: `Bearer ${token}`,
					}
				);

				// Refresh user data
				await dispatch(getUser({ username: currentUser.username, token }));

				setNotification({ 
					type: 'success', 
					message: 'Profile updated successfully!' 
				});
				
				// Clear notification after 3 seconds
				setTimeout(() => setNotification({ type: '', message: '' }), 3000);

				return true;
			} catch (err) {
				console.error('Error updating profile:', err);
				setNotification({ 
					type: 'error', 
					message: err.message || 'Failed to update profile' 
				});
				return false;
			} finally {
				setLoading(false);
			}
		},

		// Track which card is in edit mode
		isCardEditMode: (cardName) => editMode[cardName] || false,

		toggleCardEditMode: (cardName) => {
			setEditMode(prev => ({ 
				...prev, 
				[cardName]: !prev[cardName] 
			}));
		},

		cancelCardEdit: (cardName) => {
			setEditMode(prev => ({ 
				...prev, 
				[cardName]: false 
			}));
		},

		// Backward compatibility for old field-based editing (to prevent errors)
		updateField: async (fieldKey, newValue) => {
			setLoading(true);
			setNotification({ type: '', message: '' });

			try {
				// Update user profile via API
				await SublyApi.request(
					`users/${currentUser.username}`,
					{ [fieldKey]: newValue },
					'patch',
					{
						Authorization: `Bearer ${token}`,
					}
				);

				// Refresh user data
				await dispatch(getUser({ username: currentUser.username, token }));

				setNotification({ 
					type: 'success', 
					message: 'Profile updated successfully!' 
				});
				
				// Clear edit mode for this field
				setEditMode(prev => ({ ...prev, [fieldKey]: false }));

				// Clear notification after 3 seconds
				setTimeout(() => setNotification({ type: '', message: '' }), 3000);

				return true;
			} catch (err) {
				console.error('Error updating profile:', err);
				setNotification({ 
					type: 'error', 
					message: err.message || 'Failed to update profile' 
				});
				return false;
			} finally {
				setLoading(false);
			}
		},

		isEditMode: (fieldKey) => editMode[fieldKey] || false,

		toggleEditMode: (fieldKey) => {
			setEditMode(prev => ({ 
				...prev, 
				[fieldKey]: !prev[fieldKey] 
			}));
		},

		cancelEdit: (fieldKey) => {
			setEditMode(prev => ({ 
				...prev, 
				[fieldKey]: false 
			}));
		},

		// Add unified image management and context flags
		clientImages,
		isOnboarding: false, // Profile mode
		isLoading: loading
	};

	// Debug: Log current user data
	console.log('🔵 ProfileEnhanced - About to check currentUser for rendering');
	console.log('🔵 ProfileEnhanced - currentUser data:', currentUser);

	// Create a transformed user object for profile cards that handles both camelCase and snake_case
	const transformedUser = currentUser ? {
		...currentUser,
		// Ensure camelCase fields exist for profile cards
		firstName: currentUser.firstName || currentUser.first_name || '',
		lastName: currentUser.lastName || currentUser.last_name || '',
		profileCompletionDate: currentUser.profileCompletionDate || currentUser.profile_completion_date || null,
	} : null;

	console.log('🔵 ProfileEnhanced - transformedUser for cards:', transformedUser);
	console.log('🔵 ProfileEnhanced - specific fields:', {
		firstName: transformedUser?.firstName,
		lastName: transformedUser?.lastName,
		email: transformedUser?.email,
		phone: transformedUser?.phone,
		username: transformedUser?.username
	});
	
	// Check if we have required data
	if (!transformedUser?.username) {
		console.error('🔴 ProfileEnhanced - ERROR: No username in transformedUser!');
	}

	if (!currentUser) {
		console.log('🔵 ProfileEnhanced - Rendering loading state - no currentUser');
		return (
			<div className='min-h-screen bg-gray-50 flex items-center justify-center'>
				<div className='text-center'>
					<div className='w-16 h-16 border-4 border-dashed rounded-full animate-spin border-primary'></div>
					<h2 className='mt-4 font-glacial text-xl text-gray-700'>
						Loading Profile...
					</h2>
				</div>
			</div>
		);
	}

	const getInitials = () => {
		// Handle both camelCase and snake_case for existing sessions
		const firstName = currentUser?.firstName || currentUser?.first_name || '';
		const lastName = currentUser?.lastName || currentUser?.last_name || '';
		const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
		console.log('🔵 ProfileEnhanced - getInitials:', { firstName, lastName, initials });
		return initials;
	};

	console.log('🔵 ProfileEnhanced - About to render main content');
	
	return (
		<div className='min-h-screen bg-gray-50'>
			<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
				{/* Main Content */}
				<div className='max-w-4xl mx-auto'>
					{/* Header */}
					<div className='mb-8'>
						{/* Back Button */}
						<button
							onClick={() => history.push('/home')}
							className='inline-flex items-center text-sm font-montserrat-medium text-gray-600 hover:text-gray-900 transition-colors mb-6'>
							<ArrowLeft className='mr-2 h-4 w-4' />
							Back to Home
						</button>
						
						{/* Profile Info */}
						<div className='flex items-center space-x-4'>
							<div className='w-16 h-16 bg-gray-900 text-white rounded-full flex items-center justify-center text-xl font-glacial'>
								{getInitials()}
							</div>
							<div>
								<h1 className='text-3xl font-glacial font-bold text-gray-900'>
									My Profile
								</h1>
								<p className='text-sm font-montserrat-regular text-gray-600'>
									Manage your professional information and preferences
								</p>
							</div>
						</div>
						{/* Notification */}
						{notification.message && (
							<div className={`mt-4 p-4 rounded-md ${
								notification.type === 'success' 
									? 'bg-green-50 border border-green-200 text-green-800' 
									: 'bg-red-50 border border-red-200 text-red-800'
							}`}>
								<p className='text-sm font-montserrat-medium'>
									{notification.message}
								</p>
							</div>
						)}
					</div>
					<div className='space-y-6'>
						{/* Personal Information */}
						<PersonalInfoCard 
							client={transformedUser} 
							clientDataHook={profileDataHook} 
						/>

						{/* Addresses */}
						<OnboardingAddressCard 
							client={transformedUser} 
							clientDataHook={profileDataHook} 
						/>

						{/* Broker Information */}
						<BrokerInfoCard 
							client={transformedUser} 
							clientDataHook={profileDataHook} 
						/>

						{/* Photos */}
						<OnboardingPhotosCard 
							client={transformedUser} 
							clientDataHook={profileDataHook} 
						/>

						{/* Your Branding */}
						<BrandingCard 
							client={transformedUser} 
							clientDataHook={profileDataHook} 
						/>

						{/* Personal Details */}
						<PersonalDetailsCard 
							client={transformedUser} 
							clientDataHook={profileDataHook} 
						/>

						{/* Social Media & Software */}
						<SocialMediaCard 
							client={transformedUser} 
							clientDataHook={profileDataHook} 
						/>

						{/* Scheduling & Notes */}
						<SchedulingCard 
							client={transformedUser} 
							clientDataHook={profileDataHook} 
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ProfileEnhanced;